import React, { useState, useEffect } from 'react';
import { Row, Col, Card, Statistic, Typography, Segmented, Table, Tabs, Spin, message } from 'antd';
import {
  BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import { analyticsApi } from 'api/analytics';
import styled from 'styled-components';

const { Title, Text } = Typography;

const KpiCard = styled(Card)`
  border-radius: 8px;
  .ant-statistic-title { font-size: 13px; color: #8c8c8c; }
`;

const ChartCard = styled(Card)`
  border-radius: 8px;
  margin-bottom: 16px;
  .ant-card-head-title { font-size: 15px; font-weight: 600; }
`;

const Content: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [overview, setOverview] = useState<any>(null);
  const [topPlants, setTopPlants] = useState<any[]>([]);
  const [topAnimals, setTopAnimals] = useState<any[]>([]);
  const [activityData, setActivityData] = useState<any[]>([]);
  const [activityPeriod, setActivityPeriod] = useState<string>('week');

  useEffect(() => { fetchAll(); }, []);
  useEffect(() => { fetchActivity(activityPeriod); }, [activityPeriod]);

  const fetchAll = async () => {
    try {
      setLoading(true);
      const [contentRes, plantsRes, animalsRes, activityRes] = await Promise.all([
        analyticsApi.getContentOverview(),
        analyticsApi.getTopSpecies('plant', 20),
        analyticsApi.getTopSpecies('animal', 20),
        analyticsApi.getContentActivity(90, 'week'),
      ]);
      setOverview(contentRes.data);
      setTopPlants(plantsRes.data.data || []);
      setTopAnimals(animalsRes.data.data || []);
      setActivityData(activityRes.data.data || []);
    } catch (err) {
      message.error('Failed to load content data');
    } finally {
      setLoading(false);
    }
  };

  const fetchActivity = async (period: string) => {
    try {
      const res = await analyticsApi.getContentActivity(180, period);
      setActivityData(res.data.data || []);
    } catch (err) {
      console.error('Failed to fetch activity data:', err);
    }
  };

  if (loading) {
    return <div style={{ display: 'flex', justifyContent: 'center', padding: 80 }}><Spin size="large" /></div>;
  }

  const typePieData = overview ? [
    { name: 'Plant Collections', value: overview.plant_collections, color: '#1a7a4f' },
    { name: 'Animal Collections', value: overview.animal_collections, color: '#3d9970' },
  ].filter(d => d.value > 0) : [];

  const speciesColumns = [
    { title: '#', render: (_: any, __: any, i: number) => i + 1, width: 50 },
    { title: 'Common Name', dataIndex: 'common_name', key: 'common_name',
      render: (val: any) => Array.isArray(val) ? val[0] : (val || 'Unknown') },
    { title: 'Scientific Name', dataIndex: 'scientific_name', key: 'scientific_name',
      render: (val: any) => <em>{Array.isArray(val) ? val[0] : (val || 'Unknown')}</em> },
    { title: 'Count', dataIndex: 'count', key: 'count', sorter: (a: any, b: any) => a.count - b.count },
  ];

  return (
    <div>
      <Title level={2}>Content Analytics</Title>

      {/* KPI Cards */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={12} sm={8} lg={4}>
          <KpiCard><Statistic title="Plant Collections" value={overview?.plant_collections || 0} /></KpiCard>
        </Col>
        <Col xs={12} sm={8} lg={4}>
          <KpiCard><Statistic title="Animal Collections" value={overview?.animal_collections || 0} /></KpiCard>
        </Col>
        <Col xs={12} sm={8} lg={4}>
          <KpiCard><Statistic title="Plant Items" value={overview?.total_plant_items || 0} /></KpiCard>
        </Col>
        <Col xs={12} sm={8} lg={4}>
          <KpiCard><Statistic title="Animal Items" value={overview?.total_animal_items || 0} /></KpiCard>
        </Col>
        <Col xs={12} sm={8} lg={4}>
          <KpiCard><Statistic title="Shared Collections" value={overview?.shared_collections || 0} /></KpiCard>
        </Col>
        <Col xs={12} sm={8} lg={4}>
          <KpiCard><Statistic title="Uploaded Lists" value={overview?.uploaded_collections || 0} /></KpiCard>
        </Col>
      </Row>

      {/* Activity Chart + Type Breakdown */}
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={24} lg={16}>
          <ChartCard
            title="Activity Over Time"
            extra={
              <Segmented
                options={[
                  { label: 'Daily', value: 'day' },
                  { label: 'Weekly', value: 'week' },
                  { label: 'Monthly', value: 'month' },
                ]}
                value={activityPeriod}
                onChange={(val) => setActivityPeriod(val as string)}
                size="small"
              />
            }
          >
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={activityData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" tick={{ fontSize: 11 }} tickFormatter={(val) => val.slice(5)} />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="species_added" name="Species Added" fill="#1a7a4f" stackId="a" />
                <Bar dataKey="collection_created" name="Collections Created" fill="#3d9970" stackId="a" />
                <Bar dataKey="achievement_unlocked" name="Achievements" fill="#5bb88a" stackId="a" />
                <Bar dataKey="followed_user" name="Follows" fill="#93d4b0" stackId="a" />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>
        </Col>
        <Col xs={24} lg={8}>
          <ChartCard title="Collections by Type">
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie data={typePieData} cx="50%" cy="50%" innerRadius={60} outerRadius={100}
                  dataKey="value" nameKey="name"
                  label={({ name, percent }: any) => `${name.split(' ')[0]} ${((percent || 0) * 100).toFixed(0)}%`}
                >
                  {typePieData.map((entry, i) => (
                    <Cell key={i} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </ChartCard>
        </Col>
      </Row>

      {/* Top Species */}
      <Row gutter={[16, 16]}>
        <Col xs={24} lg={12}>
          <ChartCard title="Top Plants">
            <Table
              dataSource={topPlants}
              columns={speciesColumns}
              rowKey="_id"
              size="small"
              pagination={{ pageSize: 10 }}
            />
          </ChartCard>
        </Col>
        <Col xs={24} lg={12}>
          <ChartCard title="Top Animals">
            <Table
              dataSource={topAnimals}
              columns={speciesColumns}
              rowKey="_id"
              size="small"
              pagination={{ pageSize: 10 }}
            />
          </ChartCard>
        </Col>
      </Row>

      {/* Collections per User Distribution */}
      {overview?.collections_per_user && (
        <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
          <Col xs={24}>
            <ChartCard title="Collections per User Distribution">
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={overview.collections_per_user}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="collections" label={{ value: 'Collections', position: 'insideBottom', offset: -5 }} />
                  <YAxis label={{ value: 'Users', angle: -90, position: 'insideLeft' }} />
                  <Tooltip />
                  <Bar dataKey="users" fill="#1a7a4f" name="Users" />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>
          </Col>
        </Row>
      )}
    </div>
  );
};

export default Content;
